import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Package, 
  MessageSquare, 
  Star, 
  TrendingUp,
  ArrowRight,
  Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    contacts: 0,
    new_contacts: 0,
    testimonials: 0
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, messagesRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/stats`),
          axios.get(`${API_URL}/api/admin/contacts`)
        ]);
        setStats(statsRes.data);
        setRecentMessages(messagesRes.data.contacts.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    { label: 'Produse', value: stats.products, icon: Package, color: 'from-blue-500 to-blue-600', link: '/admin/products' },
    { label: 'Mesaje Noi', value: stats.new_contacts, icon: Mail, color: 'from-amber-500 to-amber-600', link: '/admin/messages' },
    { label: 'Total Mesaje', value: stats.contacts, icon: MessageSquare, color: 'from-green-500 to-green-600', link: '/admin/messages' },
    { label: 'Testimoniale', value: stats.testimonials, icon: Star, color: 'from-purple-500 to-purple-600', link: '/admin/testimonials' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Bine ai venit în panoul de administrare</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-slate-600 transition-colors group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-600 group-hover:text-slate-400 transition-colors" />
            </div>
            <p className="text-3xl font-bold text-white">{stat.value}</p>
            <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Recent Messages */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Mesaje Recente</h2>
          <Link to="/admin/messages" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1">
            Vezi toate <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        {recentMessages.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Nu ai mesaje încă
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {recentMessages.map((message) => (
              <div key={message.id} className="p-4 hover:bg-slate-700/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-white">{message.name}</p>
                      {message.status === 'new' && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">Nou</span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm truncate">{message.message}</p>
                    <p className="text-slate-500 text-xs mt-1">{message.email}</p>
                  </div>
                  <p className="text-slate-500 text-xs whitespace-nowrap">
                    {new Date(message.created_at).toLocaleDateString('ro-RO')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link 
          to="/admin/products" 
          className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-sky-500/50 transition-colors text-center"
        >
          <Package className="w-8 h-8 text-sky-400 mx-auto mb-3" />
          <p className="text-white font-medium">Adaugă Produs</p>
          <p className="text-slate-400 text-sm mt-1">Gestionează catalogul</p>
        </Link>
        
        <Link 
          to="/admin/testimonials" 
          className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-sky-500/50 transition-colors text-center"
        >
          <Star className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <p className="text-white font-medium">Adaugă Testimonial</p>
          <p className="text-slate-400 text-sm mt-1">Recenzii clienți</p>
        </Link>
        
        <Link 
          to="/admin/settings" 
          className="bg-slate-800 rounded-2xl p-6 border border-slate-700 hover:border-sky-500/50 transition-colors text-center"
        >
          <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-3" />
          <p className="text-white font-medium">Setări Site</p>
          <p className="text-slate-400 text-sm mt-1">Personalizează conținutul</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
